import React, {
  useCallback,
  useEffect,
  useLayoutEffect,
  useState
} from 'react';
import makeStyles from '@material-ui/core/styles/makeStyles';
import {
  Divider,
  Grid,
  Grow,
  Typography,
  useMediaQuery
} from '@material-ui/core';
import * as yup from 'yup';
import moment from 'moment';
import TextInput from '../Inputs/TextInput';
import InsuranceDetailDialog from '../Dialogs/InsuranceDetailDialog';

import { moneyWithDots } from '../../Utils/Format/MoneyFormat';
import ControlledSelectInput from '../Controlled/ControlledSelect';
import { useForm } from 'react-hook-form';
import CustomToggleButton from '../Buttons/ToggleButton';
import clsx from 'clsx';
import InformationCircle from '../CustomIcons/InformationCircle';
import SilverPlanCardImage from '../../Assets/images/plan_silver_insurance_card_image.jpg';
import { yupResolver } from '@hookform/resolvers/yup';

const insuranceDataSchema = yup.object({
  dateOfBirth: yup
    .date()
    .max(new Date(), 'Fecha no valida')
    .typeError('La fecha  debe ser válida')
    .required('Elija una fecha valida')
    .test('minAge', 'Debe ser mayor de edad', val => {
      return val && moment(val).isSameOrBefore(moment().subtract(18, 'y'));
    })
    .test('maxAge', 'Debe ser menor de 65 años', val => {
      return val && moment(val).isSameOrAfter(moment().subtract(65, 'y'));
    }),
  expeditionDate: yup
    .date()
    .max(new Date(), 'Fecha no valida')
    .typeError('La fecha  debe ser válida')
    .required('Elija una fecha valida'),
  gender: yup.string().required('Elija un sexo')
});

const defaultValues = {
  gender: '',
  dateOfBirth: '',
  expeditionDate: ''
};

const InsuranceAcquisitionCard = props => {
  const {
    setDisabledNext,
    price,
    setInsuranceInfo,
    setShowPlanInfo,
    showPlanInfo
  } = props;

  const classes = useStyles();

  // * STATES

  const [openInsuranceDetail, setOpenInsuranceDetail] = useState(false);
  const isMobileSize = useMediaQuery(theme =>
    theme.breakpoints.down(theme.breakpoints.values.sm)
  );

  const { register, watch, errors, control, setValue } = useForm({
    resolver: yupResolver(insuranceDataSchema),
    mode: 'onChange',
    reValidateMode: 'onChange',
    defaultValues
  });

  const { dateOfBirth, expeditionDate, gender } = watch([
    'dateOfBirth',
    'expeditionDate',
    'gender'
  ]);

  const scrollToBottom = () => {
    window.scrollTo(0, document.body.scrollHeight);
  };

  useLayoutEffect(() => {
    if (showPlanInfo) {
      scrollToBottom();
    }
  }, [showPlanInfo]);

  const handlePlanInfo = useCallback(() => {
    setShowPlanInfo(prev => !prev);
  }, [setShowPlanInfo]);

  const SELECT_OPTIONS = [
    {
      label: 'Masculino',
      value: 'M'
    },
    { label: 'Femenino', value: 'F' }
  ];

  useEffect(() => {
    if (showPlanInfo && price) {
      if (gender && dateOfBirth && expeditionDate) {
        setInsuranceInfo({
          gender,
          dateOfBirth,
          expeditionDate,
          price
        });
        return;
      }
    }
  }, [
    showPlanInfo,
    price,
    dateOfBirth,
    expeditionDate,
    gender,
    setInsuranceInfo
  ]);

  useEffect(() => {
    if (!showPlanInfo) {
      Object.keys(defaultValues).forEach(key => {
        setValue(key, defaultValues[key]);
      });
      setDisabledNext(false);
      return;
    }

    if (!gender || !dateOfBirth || !expeditionDate) {
      setDisabledNext(true);
      return;
    }
    try {
      insuranceDataSchema.validateSync(
        { gender, dateOfBirth, expeditionDate },
        { abortEarly: false }
      );
      setDisabledNext(false);
    } catch (err) {
      setDisabledNext(false);
    }
  }, [
    showPlanInfo,
    price,
    dateOfBirth,
    expeditionDate,
    gender,
    setDisabledNext,
    setValue
  ]);

  const handleOpenInsuranceDetail = useCallback(() => {
    setOpenInsuranceDetail(open => !open);
  }, [setOpenInsuranceDetail]);

  return (
    <Grid className={classes.root}>
      <Grow in>
        <Grid container className={classes.card}>
          <Grid
            container
            className={clsx(classes.insuranceCardContent, {
              [classes.planSelected]: showPlanInfo
            })}
          >
            <Grid
              item
              xs={12}
              sm={4}
              className={clsx(classes.cardImageWrapper, {
                [classes.selectedImageWrapper]: showPlanInfo
              })}
            >
              <img
                className={classes.silverPlanCardImage}
                src={SilverPlanCardImage}
                alt="silver-plan-insurance"
              />
              <span className={classes.overlayCardText}>Plan Silver</span>
            </Grid>
            <Grid
              item
              container
              direction="column"
              xs={12}
              sm={8}
              className={classes.planDetails}
            >
              <Grid item container alignItems="center">
                <Grid item xs={12} sm={7}>
                  <Typography className={classes.sectionTitle}>
                    Protege a tu familia desde hoy por sólo{' '}
                    {moneyWithDots(price)}
                    <span className={classes.subText}>/mes</span>
                  </Typography>
                </Grid>
                <Grid item xs={12} sm={5}>
                  <CustomToggleButton
                    selected={showPlanInfo}
                    disabled={!price}
                    onChange={handlePlanInfo}
                    textOff="Activar protección"
                    textOn="Protección activada"
                    textHover="No quiero protección"
                    fullWidth={isMobileSize}
                  />
                </Grid>
              </Grid>
              <Divider />
              <Grid item>
                <ul className={classes.list}>
                  <li>Cobertura por fallecimiento, sin periodo de carencias</li>
                  <li>Bajo costo</li>
                  <li>Pago cómodo en tu factura de gas</li>
                </ul>
              </Grid>
              <Grid item className={classes.valueInfo}>
                <Typography className={classes.label}>
                  En caso de ser aprobado, este valor se incluirá en tu factura
                  a partir del siguiente mes{' '}
                  <span>
                    <InformationCircle className={classes.infoIcon} />
                  </span>
                </Typography>
              </Grid>
            </Grid>
          </Grid>
          <Grid container>
            {showPlanInfo && (
              <form className={classes.planUserDataForm}>
                <TextInput
                  label="Fecha de nacimiento"
                  name="dateOfBirth"
                  type="date"
                  inputRef={register}
                  className={
                    errors && Boolean(errors.dateOfBirth)
                      ? classes.inputError
                      : classes.input
                  }
                  fullWidth={true}
                  InputLabelProps={{ shrink: true }}
                  error={Boolean(errors.dateOfBirth)}
                  helperText={errors.dateOfBirth && errors.dateOfBirth.message}
                />
                <TextInput
                  label="Fecha de expedición identificación"
                  name="expeditionDate"
                  type="date"
                  inputRef={register}
                  className={
                    errors && Boolean(errors.expeditionDate)
                      ? classes.inputError
                      : classes.input
                  }
                  fullWidth={true}
                  InputLabelProps={{ shrink: true }}
                  error={Boolean(errors.expeditionDate)}
                  helperText={
                    errors.expeditionDate && errors.expeditionDate.message
                  }
                />
                <ControlledSelectInput
                  name="gender"
                  control={control}
                  error={Boolean(errors.gender)}
                  helperText={errors.gender}
                  className={
                    errors && Boolean(errors.gender)
                      ? classes.inputError
                      : classes.input
                  }
                  label="Género"
                  fullWidth
                  options={SELECT_OPTIONS}
                />
              </form>
            )}
          </Grid>
        </Grid>
      </Grow>
      {openInsuranceDetail && (
        <InsuranceDetailDialog
          title="Seguro de vida"
          open={true}
          onClose={handleOpenInsuranceDetail}
          adquire={handlePlanInfo}
          adquired={showPlanInfo}
          planPrice={price}
        />
      )}
    </Grid>
  );
};

const useStyles = makeStyles(theme => ({
  root: {
    paddingBottom: theme.spacing(2),
    [theme.breakpoints.down(theme.breakpoints.values.sm)]: {
      marginBottom: theme.spacing(12.5)
    }
  },
  card: {
    display: 'flex',
    flexDirection: 'row',
    justifyContent: 'space-between',
    borderRadius: theme.custom.borderRadius,
    backgroundColor: theme.palette.background.accordionLighter,
    position: 'relative',
    [theme.breakpoints.down(theme.breakpoints.values.sm)]: {
      flexDirection: 'column',
      marginTop: theme.spacing(1),
      marginBottom: theme.spacing(1)
    }
  },
  insuranceSection: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'flex-start',
    justifyContent: 'center'
  },
  insuranceContent: {
    display: 'flex',
    alignSelf: 'center',
    alignItems: 'center',
    justifyContent: 'space-between',
    rowGap: '8px',
    columnGap: '12px',
    [theme.breakpoints.down(theme.breakpoints.values.sm)]: {
      columnGap: '8px',
      alignSelf: 'flex-start'
    }
  },
  backIcon: {
    fill: 'currentColor',
    marginRight: theme.spacing(1)
  },
  insuranceActions: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'flex-start'
  },
  insuranceActivation: {
    display: 'flex',
    alignItems: 'center',
    gap: '16px',
    [theme.breakpoints.down(theme.breakpoints.values.sm)]: {
      gap: '8px'
    }
  },
  switch: {
    [theme.breakpoints.down(theme.breakpoints.values.sm)]: {
      margin: '0px'
    }
  },
  thum: {
    width: '25px',
    height: '25px',
    marginTop: '-3px',
    border: '1px #747b87 solid'
  },
  viewMoreText: {
    fontWeight: 600,
    gap: '8px',
    [theme.breakpoints.down(theme.breakpoints.values.sm)]: {
      fontSize: 12
    }
  },
  viewMoreIcon: {
    height: 16,
    width: 16,
    transform: 'scaleX(-1)'
  },
  valueSection: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'flex-end',
    gap: '8px',
    justifyContent: 'center',
    [theme.breakpoints.down(theme.breakpoints.values.sm)]: {
      textAlign: 'right'
    }
  },
  valueInfo: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'flex-start',
    textAlign: 'left',
    gap: '8px'
  },
  infoIcon: {
    width: '16px',
    height: '16px',
    [theme.breakpoints.down(theme.breakpoints.values.sm)]: {
      width: '12px',
      height: '12px'
    }
  },
  insuranceLogo: {
    display: 'flex',
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginBottom: '18px',
    backgroundColor: theme.palette.secondary.disabled,
    padding: theme.spacing(1)
  },
  planUserDataForm: {
    display: 'flex',
    flexDirection: 'row',
    backgroundColor: theme.palette.common.white,
    justifyContent: 'space-between',
    width: '100%',
    padding: theme.spacing(2, 3, 0, 3),
    borderColor: theme.palette.background.border,
    borderStyle: 'solid',
    borderWidth: '0 1px 1px 1px',
    borderRadius: `0 0 ${theme.custom.borderRadius}px ${theme.custom.borderRadius}px`,
    gap: '16px',
    [theme.breakpoints.down(theme.breakpoints.values.sm)]: {
      flexDirection: 'column',
      gap: '8px',
      paddingBottom: theme.spacing(2)
    }
  },
  label: {
    fontSize: 10,
    fontWeight: 'bold',
    color: theme.palette.text.greyDark
  },
  data: {
    fontSize: 16,
    fontWeight: 600,
    [theme.breakpoints.down(theme.breakpoints.values.sm)]: {
      fontSize: 12
    }
  },
  total: {
    fontSize: 18,
    [theme.breakpoints.down(theme.breakpoints.values.sm)]: {
      fontSize: 16
    }
  },
  button: {
    fontSize: 13,
    textTransform: 'capitalize',
    marginRight: theme.spacing(1)
  },
  margin: {
    marginBottom: theme.spacing(3)
  },
  input: {
    marginBottom: theme.spacing(2),
    [theme.breakpoints.down(theme.breakpoints.values.sm)]: {
      marginBottom: theme.spacing(1)
    }
  },
  inputError: {
    marginBottom: 0
  },
  sectionTitle: {
    fontSize: 18,
    textAlign: 'left',
    fontWeight: '700',
    color: theme.palette.common.black,
    [theme.breakpoints.down(theme.breakpoints.values.sm)]: {
      fontSize: 16,
      paddingBottom: theme.spacing(2)
    }
  },
  subText: {
    fontSize: 10
  },
  insuranceCardContent: {
    display: 'flex',
    flexDirection: 'row',
    border: `1px solid ${theme.palette.background.border}`,
    borderRadius: theme.custom.borderRadius,
    [theme.breakpoints.down(theme.breakpoints.values.sm)]: {
      flexDirection: 'column'
    }
  },
  planSelected: {
    borderWidth: '1px 1px 0 1px',
    borderRadius: `${theme.custom.borderRadius}px ${theme.custom.borderRadius}px 0 0`
  },
  list: {
    color: theme.typography.color.grey,
    paddingLeft: 0,
    listStylePosition: 'inside',
    fontWeight: 500,
    fontSize: 14,
    [theme.breakpoints.down(theme.breakpoints.values.sm)]: {
      fontSize: 12
    }
  },
  planDetails: {
    padding: theme.spacing(2)
  },
  silverPlanCardImage: {
    width: '100%',
    height: 202,
    display: 'block',
    objectFit: 'cover',
    [theme.breakpoints.down(theme.breakpoints.values.sm)]: {
      height: 120
    }
  },
  cardImageWrapper: {
    position: 'relative',
    width: '100%',
    overflow: 'hidden',
    borderRadius: `${theme.custom.borderRadius}px 0 0 ${theme.custom.borderRadius}px`,
    [theme.breakpoints.down(theme.breakpoints.values.sm)]: {
      borderRadius: `${theme.custom.borderRadius}px ${theme.custom.borderRadius}px 0 0`
    }
  },
  selectedImageWrapper: {
    borderRadius: `${theme.custom.borderRadius}px 0 0 0`
  },
  overlayCardText: {
    position: 'absolute',
    bottom: '10px',
    right: '10px',
    backgroundColor: '#99F2F9',
    color: '#306468',
    padding: '4px 8px',
    borderRadius: theme.custom.borderRadius,
    fontSize: 12,
    fontWeight: 'bold',
    zIndex: 1
  }
}));

export default InsuranceAcquisitionCard;
